import { z } from 'zod';
import { Margin } from './margin.js';
const amount = z.number().finite().nonnegative().max(1e8);
export const BasePricingInput = z.object({ products: z.array(z.object({ productId: z.string().min(1).max(160), variantId: z.string().max(160).optional(), mode: z.enum(['supply', 'supply_install', 'install']), quantity: amount.positive(), area: amount, ruleId: z.string().max(100).optional(), manualSell: amount.optional(),
        /** A per-unit selling price ALREADY DECIDED for this line (domain 0.5.5). It wins over the model's
         *  markups, so a price the customer was shown — an alternative listed at the proposal's rate —
         *  survives the line being repriced, which is exactly what happens when they choose it. AIW stores
         *  it on the selection; the engine is the one that applies it. */
        sellOverride: amount.optional(), accessoryCost: amount.optional(), thickerUnderlay: z.boolean().optional(), shapeConfirmed: z.boolean().optional() })).max(100), services: z.array(z.object({ rateId: z.string().max(100), quantity: amount, customerSkip: z.boolean().optional(), manualSell: amount.optional() })).max(100), access: z.object({ kind: z.enum(['house', 'elevator', 'stairs', 'above3']), weightKg: amount, unloadParking: z.boolean() }).optional(), minimum: z.string().max(100).optional() });
/** The colour decision that has not been made yet. It names the reason, not a
 *  system state, and it is the same words on both copies — staff and customer.
 *  Never "unknown", never "TBC", never blank. */
export const UNDECIDED_COLOUR = 'Customer not yet decided';
/**
 * AIW's own layer over the engine's input. The engine is given `BasePricingInput`
 * and nothing else (ADR-004) — these two fields are what the building trade reads
 * back on the screen, so they are stored with the selection and stripped before
 * pricing:
 *   · `rooms`  — which assessed rooms this line covers; the customer's area comes
 *                from them, so an area is never typed twice and never disagrees.
 *   · `colour` — the colour as written on the sample, or the undecided sentence.
 */
export const ProposalSelection = BasePricingInput.shape.products.element.extend({
    id: z.string().min(1).max(80),
    /** Commercial edits never mutate factual assessment measurements. Legacy selections remain manual. */
    replaces_id: z.string().min(1).max(80).optional(),
    description: z.string().trim().max(300).optional(),
    quantity_source: z.enum(['manual', 'assessment']).optional(),
    scope_ids: z.array(z.string().max(160)).max(100).optional(),
    measured_quantity: amount.optional(),
    quantity_multiplier: z.number().finite().min(1).max(2).optional(),
    rooms: z.array(z.string().trim().max(120)).max(100).default([]),
    colour: z.string().trim().max(160).default(UNDECIDED_COLOUR),
    /** Full carpet roll width. The engine still costs whole LM; the customer pays purchased m². */
    carpetRollWidthM: z.number().positive().max(20).optional(),
    /** The sub-category this line belongs to — "Laminate", "Hybrid", "Carpet". Jacky's model is
     *  *"I define multiple sub category such as Laminate | Hybrid and I pick one from each"*, so a
     *  proposal is a set of GROUPS with a pick in each, not a flat list. Empty means the line was
     *  added before grouping existed, and it is shown under its product's own sub-type. */
    group: z.string().trim().max(60).default(''),
    /** `recommended` is the product he (or EmmA) puts forward and what the total is built from.
     *  `option` is a product the CUSTOMER may choose instead, listed at the proposal's rate and
     *  deliberately NOT in the total — a choice is not a purchase. */
    role: z.enum(['recommended', 'option']).default('recommended'),
});
export const ProposalPricing = BasePricingInput.extend({ charge_rounding: z.literal('line_down').optional(),
    products: z.array(ProposalSelection).max(100),
    /** The rate the OPTION pool is listed at. Null means no rate set, and options price as the engine
     *  prices them. Never applied to a recommended line: his decision, 17 September. */
    margin: Margin.nullable().default(null),
});
/** The engine input, with AIW's own two fields dropped. `z.object` strips the
 *  keys it does not know, so this is the one place the boundary is crossed. */
export function enginePricing(pricing) { return BasePricingInput.parse(pricing); }
/** An empty selection for a product the picker has just chosen. Carpet is costed
 *  by the engine in LM; its customer quantity is whole LM times full roll width. */
export function newSelection(id, productId) {
    return ProposalSelection.parse({ id, productId, mode: 'supply_install', quantity: 1, area: 0 });
}
/**
 * A room as a DRAWING records it — the job canvas and an opportunity's traced plan use one shape.
 *
 * Jacky, 17 September: *"Both — the opportunity carries the plan, the job carries the measured
 * drawing, and the Proposal reads whichever exists."* Two anchors, one room: boundary in METRES (the
 * studio draws to scale), the floor it sits on, whether it was measured on site, the seam alignment
 * where two runs were joined, and the laser survey when there is one. `services/api/src/canvas-rooms.ts`
 * maps either anchor into the Proposal's assessment with the same rules.
 */
export const CanvasRoom = z.object({
    room_id: z.string().trim().max(120).default(''),
    name: z.string().trim().min(1).max(120),
    floor: z.string().trim().max(60).default(''),
    boundary: z.array(z.object({ x: z.number().finite(), y: z.number().finite() })).max(200).default([]),
    members: z.array(z.string().trim().max(120)).max(40).default([]),
    measured: z.boolean().default(false),
    seamSnapMM: z.number().finite().nullable().default(null),
    survey: z.record(z.string(), z.unknown()).nullable().default(null),
});
//# sourceMappingURL=base-pricing.js.map